import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Panel } from "@/components/studio/Fields";
import { getAnalytics } from "@/lib/site";

/* What the counter can honestly say, and what it cannot.

   No identifier is stored on the visitor's device, so there is no such thing
   as a returning visitor here. Rather than print a "unique visitors" number
   that would be a guess, the panel names what it measures. */

const RANGES = [7, 30, 90];

const Metric = ({ label, value, hint }) => (
  <div className="rounded-lg border p-4" style={{ borderColor: "var(--line)" }}>
    <p className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">{label}</p>
    <p className="font-num mt-1.5 text-[28px] leading-none">{value}</p>
    {hint ? <p className="mt-1.5 text-[11px] text-[color:var(--muted-ink)]">{hint}</p> : null}
  </div>
);

const Bars = ({ series }) => {
  const peak = Math.max(1, ...series.map((row) => row.views));
  return (
    <div className="mt-2 flex h-32 items-end gap-[3px]" data-testid="audience-chart">
      {series.map((row) => (
        <div
          key={row.day}
          title={`${row.day} · ${row.views}`}
          className="flex-1 rounded-t-sm"
          style={{
            height: `${Math.max(3, (row.views / peak) * 100)}%`,
            backgroundColor: "var(--sage)",
            minWidth: "3px",
          }}
        />
      ))}
    </div>
  );
};

const TopList = ({ title, rows, empty }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">{title}</p>
    {rows?.length ? (
      <ul className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <li key={row.label} className="flex items-baseline justify-between gap-3 text-[13px]">
            <span className="truncate text-[color:var(--ink-soft)]">{row.label}</span>
            <span className="font-num text-[12px]">{row.views}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="mt-2 text-[12px] text-[color:var(--muted-ink)]">{empty}</p>
    )}
  </div>
);

export const AudiencePanel = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getAnalytics(days)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days]);

  const minutes = data ? Math.floor(data.avg_seconds / 60) : 0;
  const seconds = data ? data.avg_seconds % 60 : 0;

  return (
    <Panel
      title="Audience"
      hint="Sans cookie, sans identifiant déposé sur la machine du visiteur, sans adresse IP conservée. C’est pour cela que le site n’a pas de bandeau de consentement — et aussi pourquoi il ne peut pas compter les visiteurs qui reviennent."
      actions={
        <div className="inline-flex gap-0.5 rounded-full p-0.5" style={{ backgroundColor: "var(--paper-2)" }}>
          {RANGES.map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => setDays(range)}
              aria-pressed={days === range}
              data-testid={`range-${range}`}
              className="rounded-full px-3 py-1 text-[12px] font-medium"
              style={{
                backgroundColor: days === range ? "#ffffff" : "transparent",
                color: days === range ? "var(--ink)" : "var(--muted-ink)",
              }}
            >
              {range} j
            </button>
          ))}
        </div>
      }
    >
      {loading ? (
        <p className="flex items-center gap-2 text-[13px] text-[color:var(--muted-ink)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
        </p>
      ) : !data ? (
        <p className="text-[13px] text-[color:var(--ink-soft)]">
          Les statistiques ne sont pas disponibles pour le moment.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Metric label="Pages vues" value={data.views} />
            <Metric
              label="Visites"
              value={data.visits}
              hint="Un onglet ouvert, quel que soit le nombre de pages"
            />
            <Metric
              label="Durée moyenne"
              value={data.avg_seconds ? `${minutes}′${String(seconds).padStart(2, "0")}″` : "—"}
              hint={`Mesurée sur ${data.measured_share} % des pages vues`}
            />
            <Metric
              label="Conservation"
              value={`${data.retention_days} j`}
              hint="Après quoi la ligne disparaît d’elle-même"
            />
          </div>

          {data.by_day?.length ? (
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--muted-ink)]">
                Jour par jour
              </p>
              <Bars series={data.by_day} />
              <div className="mt-1 flex justify-between text-[10.5px] text-[color:var(--muted-ink)]">
                <span>{data.by_day[0]?.day}</span>
                <span>{data.by_day[data.by_day.length - 1]?.day}</span>
              </div>
            </div>
          ) : null}

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            <TopList title="Pages" rows={data.pages} empty="Aucune page vue." />
            <TopList title="Provenance" rows={data.sources} empty="Aucune source." />
            <TopList title="Régions" rows={data.regions} empty="Aucune région." />
            <TopList title="Langue / écran" rows={[...(data.languages || []), ...(data.screens || [])]} empty="—" />
          </div>

          <p className="mt-6 text-[11.5px] leading-relaxed text-[color:var(--muted-ink)]">
            La région vient du fuseau horaire que le navigateur annonce lui-même, pas d’une
            recherche sur l’adresse IP : « Europe/Paris » devient « France », et un fuseau
            inconnu s’arrête à son continent. Pour une localisation plus fine, il faudra le
            service d’analytique que vous choisirez — l’emplacement pour le brancher est dans
            l’onglet Réglages.
          </p>
        </>
      )}
    </Panel>
  );
};

export default AudiencePanel;
